import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Briefcase, BarChart3, BookOpen, Zap, ArrowRight, FileText, TrendingUp, CheckCircle2, Sparkles } from 'lucide-react';

export default function Services() {
  const services = [
    {
      icon: <Users className="text-blue-600" size={40} />,
      title: 'Executive & Professional Recruitment',
      description: 'Top-tier talent acquisition for leadership and management positions across all sectors.'
    },
    {
      icon: <Briefcase className="text-green-600" size={40} />,
      title: 'Skilled & Semi-Skilled Workforce',
      description: 'Technical and specialized professionals for diverse industry requirements.'
    },
    {
      icon: <BarChart3 className="text-purple-600" size={40} />,
      title: 'Mining & Industrial Sector',
      description: 'Specialized recruitment for Mining, Energy, Construction, and Industrial sectors.'
    },
    {
      icon: <BookOpen className="text-orange-600" size={40} />,
      title: 'HR Advisory & Consulting',
      description: 'Strategic HR advisory, talent consulting, and organizational development.'
    },
    {
      icon: <Zap className="text-red-600" size={40} />,
      title: 'Screening & Due Diligence',
      description: 'Comprehensive background checks, verification, and workforce screening services.'
    },
    {
      icon: <ArrowRight className="text-indigo-600" size={40} />,
      title: 'Talent Outsourcing',
      description: 'Contract staffing and flexible workforce solutions for project-based needs.'
    },
    {
      icon: <FileText className="text-teal-600" size={40} />,
      title: 'Market Surveys',
      description: 'In-depth market research and salary benchmarking for informed decisions.'
    },
    {
      icon: <TrendingUp className="text-pink-600" size={40} />,
      title: 'Career Guidance',
      description: 'Professional career counseling and development support for job seekers.'
    }
  ];

  const highlights = [
    'Fast turnaround with curated shortlists',
    'Verified candidates and compliance-ready checks',
    'Dedicated account management and support'
  ];

  const stats = [
    { label: 'Industries Covered', value: '25+' },
    { label: 'Candidate Network', value: '50k+' },
    { label: 'Placement Success', value: '92%' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="relative overflow-hidden bg-gradient-to-r from-blue-950 via-blue-900 to-blue-700 text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #ffffff 1px, transparent 0), radial-gradient(circle at 80% 30%, #ffffff 1px, transparent 0)' }} />
        <div className="relative max-w-6xl mx-auto py-20 px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white px-4 py-2 rounded-full text-sm mb-6">
                <Sparkles size={16} />
                Talent Link Employment Agency
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Recruitment that powers growth</h1>
              <p className="text-blue-100 text-lg mb-8">
                End-to-end hiring and workforce solutions built for speed, quality, and long-term fit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/post-job" className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition">
                  Post a Job
                  <ArrowRight size={18} />
                </Link>
                <Link to="/jobs" className="inline-flex items-center justify-center gap-2 bg-blue-700/50 border border-white/30 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700/70 transition">
                  Browse Vacancies
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            <div className="flex-1 w-full">
              <div className="grid grid-cols-3 gap-4">
                {stats.map((stat) => (
                  <div key={stat.label} className="bg-white/10 border border-white/20 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                    <div className="text-blue-100 text-sm mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 bg-white/10 border border-white/20 rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-4">Why clients choose us</h3>
                <ul className="space-y-3">
                  {highlights.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-blue-100">
                      <CheckCircle2 size={18} className="text-white mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">Services tailored to your sector</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">From executive search to bulk staffing, our services scale with your hiring needs.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div key={idx} className="group bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition relative overflow-hidden">
                <div className="absolute -right-10 -top-10 w-24 h-24 rounded-full bg-blue-50 group-hover:scale-125 transition" />
                <div className="relative">
                  <div className="w-14 h-14 rounded-xl bg-gray-50 flex items-center justify-center mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="flex items-center text-blue-700 font-semibold">
                    Learn more <ArrowRight size={18} className="ml-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4">A hiring partner you can trust</h2>
            <p className="text-gray-600 mb-6">
              We combine market insight, industry specialization, and a strong candidate network to deliver results quickly without sacrificing quality.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {['Candidate screening', 'Talent mapping', 'Contract staffing', 'Onboarding support'].map((item) => (
                <div key={item} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-lg p-4">
                  <CheckCircle2 size={18} className="text-green-600" />
                  <span className="text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-2xl p-8 shadow-xl">
            <h3 className="text-xl font-bold mb-3">Ready to get started?</h3>
            <p className="text-gray-600 mb-6">Tell us about your hiring goals and we’ll propose the best plan.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition">
                Create Employer Account
                <ArrowRight size={18} />
              </Link>
              <a href="mailto:info@talentlink.co.zw" className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 border border-blue-200 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition">
                info@talentlink.co.zw
              </a>
              <a href="tel:+263784152659" className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 border border-blue-200 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition">
                +263 784 152 659
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
